import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api";
import { ErrorNote, Loading } from "../components/ui";
import { Delta, Heatmap, type PeriodName } from "../components/charts2";
import { BarSeries, TrendLine } from "../components/charts3";
import { PeriodFilters } from "../components/PeriodFilters";
import { useLocations } from "../hooks";
import type { ExecutiveOverview, Filters, HeatmapData } from "../types";

const TRENDS: PeriodName[] = ["daily", "weekly", "monthly", "quarterly", "yearly"];

export default function Executive() {
  const locations = useLocations();
  const [filters, setFilters] = useState<Filters>({});
  const [trendKey, setTrendKey] = useState<Exclude<PeriodName, "custom">>("monthly");
  const [data, setData] = useState<ExecutiveOverview | null>(null);
  const [heat, setHeat] = useState<HeatmapData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([api.overview(filters), api.heatmap(filters, 20)])
      .then(([d, h]) => { setData(d); setHeat(h); setError(null); })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filters]);

  const k = data?.kpis;
  const d = data?.previous_period?.deltas;

  return (
    <div>
      <h1 className="h1">Executive Dashboard</h1>
      <p className="sub">Office-presence analytics from processed access-card data. Metrics requiring
        roster/shift data (absent, late, overtime) are intentionally not shown.</p>

      <PeriodFilters locations={locations} initialMode="year" storageKey="executive" onChange={setFilters} />

      {loading && <Loading what="analytics" />}
      {error && <ErrorNote message={error} />}

      {data && k && !loading && (
        <>
          <div className="cards" style={{ marginTop: 18 }}>
            <Kpi label="Employees present" value={k.employees_present} delta={d?.employees_present} />
            <Kpi label="Attendance days" value={k.total_attendance_days} delta={d?.total_attendance_days} />
            <Kpi label="Total inside" value={k.total_inside_hhmm} delta={d?.total_inside_seconds} />
            <Kpi label="Avg daily" value={k.avg_daily_hhmm} delta={d?.avg_daily_seconds} />
            <Kpi label="Avg arrival" value={k.avg_arrival ?? "—"} />
            <Kpi label="Avg exit" value={k.avg_exit ?? "—"} />
            <Kpi label="Incomplete" value={k.incomplete_records} />
          </div>

          {data.data_quality.alerts.length > 0 && (
            <div className="panel">
              <h2>Data-quality alerts</h2>
              {data.data_quality.alerts.map((a, i) => <div key={i} className="notice">⚠ {a}</div>)}
            </div>
          )}

          <div className="panel">
            <h2>Data-quality trend (incomplete / review / unmatched)</h2>
            <TrendLine data={data.dq_trend[trendKey]} xKey="period" height={240}
                       lines={[
                         { key: "incomplete", name: "Incomplete", color: "#d14343" },
                         { key: "review", name: "Review", color: "#b7791f" },
                         { key: "unmatched", name: "Unmatched", color: "#8b5cf6" },
                       ]} />
          </div>

          <div className="panel">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ margin: 0 }}>Inside-office hours trend</h2>
              <select value={trendKey} onChange={(e) => setTrendKey(e.target.value as any)}>
                {TRENDS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <TrendLine data={data.trends[trendKey]} xKey="period" kind="area" unit="hours"
                       height={280} lines={[{ key: "inside_seconds", name: "Inside hours" }]} />
          </div>

          <div className="chart-grid">
            <div className="panel" style={{ marginTop: 0 }}>
              <h2>Arrival-time distribution (by hour)</h2>
              <BarSeries xKey="label" barKey="count" name="Arrivals" height={220}
                         data={data.arrival_hour_distribution.filter((h) => h.count > 0)
                           .map((h) => ({ label: `${h.hour}:00`, count: h.count }))} />
            </div>
            <div className="panel" style={{ marginTop: 0 }}>
              <h2>Weekday attendance pattern</h2>
              <BarSeries xKey="label" barKey="present_days" name="Present days" height={220}
                         data={data.weekday_pattern.map((w) => ({ label: w.name.slice(0, 3), present_days: w.present_days }))} />
            </div>
          </div>

          <div className="panel">
            <h2>Location-wise presence (inside hours)</h2>
            <BarSeries xKey="label" barKey="total_inside_seconds" name="Inside hours" unit="hours" height={220}
                       data={data.location_breakdown.map((l) => ({ label: l.key, total_inside_seconds: l.total_inside_seconds }))} />
          </div>

          <div className="panel">
            <h2>Employee attendance heatmap (top 20 by presence)</h2>
            <p className="notice">Shaded by inside-office time per day. Click a name for the full profile.</p>
            {heat && <Heatmap dates={heat.dates} employees={heat.employees}
                              onPick={(id) => navigate(`/employees/${encodeURIComponent(id)}`)} />}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, delta }: { label: string; value: ReactNode; delta?: number | null }) {
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <Delta pct={delta} />
    </div>
  );
}
